// Database types for role-based features
// Generated from Supabase schema

export type UserRole = 'Admin' | 'Lender' | 'Seller' | 'Viewer' | 'Buyer' | 'Advisor' | 'Attorney';

// Document management types
export type DocumentType = 
  | 'tax_return'
  | 'profit_loss_statement'
  | 'balance_sheet'
  | 'inventory_list'
  | 'lease_agreement'
  | 'financial_statement'
  | 'bank_statement'
  | 'insurance_document'
  | 'legal_document'
  | 'w2'
  | 'personal_financial_statement'
  | 'paystub'
  | 'other';

export type DocumentStatus = 
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'archived';

export type DocumentOwnershipType = 
  | 'buyer'
  | 'seller';

export type DocumentAccessLevel = 
  | 'private'
  | 'role_based'
  | 'shared'
  | 'public';

export type DocumentAccessLevelSimple = 
  | 'private'
  | 'shared';

export type DocumentAccessType = 'read' | 'write' | 'admin';

export type Permission = 
  | 'read_financial_data'
  | 'write_financial_data'
  | 'read_deal_scenarios'
  | 'write_deal_scenarios'
  | 'read_loi_documents'
  | 'write_loi_documents'
  | 'manage_users'
  | 'admin_access'
  | 'export_data'
  | 'view_analytics'
  | 'manage_documents'
  | 'upload_documents'
  | 'download_documents'
  | 'share_documents'
  | 'delete_documents'
  | 'edit_documents';

export interface UserProfile {
  id: string;
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  last_login?: string;
  metadata?: Record<string, any>;
}

export interface RolePermission {
  id: string;
  role: UserRole;
  permission: Permission;
  granted: boolean;
  created_at: string;
}

export interface UserSession {
  id: string;
  user_id: string;
  session_token?: string;
  ip_address?: string;
  user_agent?: string;
  role: UserRole;
  created_at: string;
  expires_at?: string;
  is_active: boolean;
}

export interface AuditLog {
  id: string;
  user_id?: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface UserManagementView {
  id: string;
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login?: string;
  email_confirmed_at?: string;
  last_sign_in_at?: string;
}

export interface RolePermissionsSummary {
  role: UserRole;
  permissions: string[];
}

export interface UserProfileWithPermissions {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  is_active: boolean;
  permissions: string[];
}

// Database function return types
export interface GetUserProfileResult {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  is_active: boolean;
  permissions: string[];
}

export interface GetAllUsersResult {
  id: string;
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

// API response types
export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface UserRoleUpdateRequest {
  user_id: string;
  new_role: UserRole;
}

export interface UserCreateRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
}

// Document management interfaces
export interface Document {
  id: string;
  title: string;
  description?: string;
  document_type: DocumentType;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  file_hash?: string;
  status: DocumentStatus;
  access_level: DocumentAccessLevel;
  ownership_type?: DocumentOwnershipType;
  uploaded_by: string;
  uploaded_at: string;
  updated_at: string;
  expires_at?: string;
  metadata?: Record<string, any>;
  version: number;
  is_active: boolean;
}

export interface DocumentAccess {
  id: string;
  document_id: string;
  user_id?: string;
  role?: UserRole;
  access_type: DocumentAccessType;
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
}

export interface DocumentSharing {
  id: string;
  document_id: string;
  shared_by: string;
  share_token: string;
  access_type: 'read' | 'download';
  expires_at: string;
  max_downloads?: number;
  download_count: number;
  is_active: boolean;
  created_at: string;
  metadata?: Record<string, any>;
}

export interface DocumentUserAccess {
  user_id: string;
  clerk_user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  access_type: string;
  status: string;
  granted_at?: string;
  granted_by?: string;
  granted_by_email?: string;
}

export interface PreviouslySharedUser {
  user_id: string;
  clerk_user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: string;
  last_shared_at: string;
}

export interface DocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_name: string;
  file_path: string;
  file_size: number;
  file_hash: string;
  uploaded_by: string;
  uploaded_at: string;
  change_notes?: string;
  is_current: boolean;
}

export interface DocumentAuditLog {
  id: string;
  document_id: string;
  user_id?: string;
  action: string;
  details?: Record<string, any>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface DocumentTag {
  type: 'document_type' | 'file_extension';
  label: string;
  color: 'blue' | 'gray';
}

export interface UserDocument {
  id: string;
  title: string;
  description?: string;
  document_type: DocumentType;
  file_name: string;
  file_size: number;
  mime_type: string;
  status: DocumentStatus;
  access_level: DocumentAccessLevel;
  access_level_simple?: DocumentAccessLevelSimple;
  ownership_type?: DocumentOwnershipType;
  uploaded_by: string;
  uploaded_at: string;
  owner_id?: string;  // Add owner_id field
  version: number;
  can_read: boolean;
  can_write: boolean;
  can_admin: boolean;
  is_duplicate: boolean;
  original_file_id?: string;
  duplicate_number: number;
  associated_deals?: DocumentDealAssociation[];
  document_tags?: DocumentTag[];
  metadata?: {
    original_filename?: string;
    extraction_id?: string;
    source?: string;
    [key: string]: any;
  };
}

export interface DocumentUploadRequest {
  title: string;
  description?: string;
  document_type: DocumentType;
  file: File;
  access_level: DocumentAccessLevel;
  expires_at?: string;
  metadata?: Record<string, any>;
}

export interface DocumentShareRequest {
  document_id: string;
  access_type: 'read' | 'download';
  expires_hours: number;
  max_downloads?: number;
}

export interface DocumentAccessRequest {
  document_id: string;
  user_id?: string;
  role?: UserRole;
  access_type: DocumentAccessType;
  expires_at?: string;
}

// Document-Deal Association interfaces
export interface DocumentDealAssociation {
  id: string;
  document_id: string;
  deal_id: string;
  deal_title?: string;
  association_type: 'specific' | 'all_deals';
  created_at: string;
  is_active: boolean;
}

export interface DocumentDealAssociationRequest {
  document_id: string;
  deal_id: string;
  association_type: 'specific' | 'all_deals';
}

// Permission checking utilities
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  Admin: [
    'read_financial_data',
    'write_financial_data',
    'read_deal_scenarios',
    'write_deal_scenarios',
    'read_loi_documents',
    'write_loi_documents',
    'manage_users',
    'admin_access',
    'export_data',
    'view_analytics',
    'manage_documents',
    'upload_documents',
    'download_documents',
    'share_documents',
    'delete_documents'
  ],
  Buyer: [
    'read_financial_data',
    'write_financial_data',
    'read_deal_scenarios',
    'write_deal_scenarios',
    'read_loi_documents',
    'write_loi_documents',
    'view_analytics',
    'export_data',
    'manage_documents',
    'upload_documents',
    'download_documents',
    'share_documents',
    'delete_documents'
  ],
  Lender: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'view_analytics',
    'export_data',
    'upload_documents',
    'download_documents',
    'share_documents'
  ],
  Seller: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'view_analytics',
    'upload_documents',
    'download_documents',
    'share_documents'
  ],
  Advisor: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'view_analytics',
    'export_data',
    'upload_documents',
    'download_documents',
    'share_documents'
  ],
  Attorney: [
    'read_financial_data',
    'read_deal_scenarios',
    'read_loi_documents',
    'write_loi_documents',
    'view_analytics',
    'export_data',
    'upload_documents',
    'download_documents',
    'share_documents',
    'manage_documents'
  ],
  Viewer: [
    'read_financial_data',
    'view_analytics',
    'download_documents'
  ]
};

export function hasPermission(userRole: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[userRole]?.includes(permission) ?? false;
}

export function getRoleDescription(role: UserRole): string {
  const descriptions: Record<UserRole, string> = {
    Admin: 'Full access to all features and user management',
    Buyer: 'Access to create and manage deals, financial data, and documents',
    Lender: 'Access to financial analysis and lending tools',
    Seller: 'Access to business valuation and performance metrics',
    Advisor: 'Access to financial analysis and advisory tools',
    Attorney: 'Access to legal documents and deal structuring tools',
    Viewer: 'Read-only access to basic financial data'
  };
  return descriptions[role] ?? 'Unknown role';
}

export function getRoleColor(role: UserRole): string {
  const colors: Record<UserRole, string> = {
    Admin: 'bg-red-100 text-red-800',
    Buyer: 'bg-purple-100 text-purple-800',
    Lender: 'bg-blue-100 text-blue-800',
    Seller: 'bg-green-100 text-green-800',
    Advisor: 'bg-orange-100 text-orange-800',
    Attorney: 'bg-indigo-100 text-indigo-800',
    Viewer: 'bg-gray-100 text-gray-800'
  };
  return colors[role] ?? 'bg-gray-100 text-gray-800';
}

// Document utility functions
export function getDocumentTypeLabel(type: DocumentType): string {
  const labels: Record<DocumentType, string> = {
    tax_return: 'Tax Return',
    profit_loss_statement: 'Profit & Loss Statement',
    balance_sheet: 'Balance Sheet',
    inventory_list: 'Inventory List',
    lease_agreement: 'Lease Agreement',
    financial_statement: 'Financial Statement',
    bank_statement: 'Bank Statement',
    insurance_document: 'Insurance Document',
    legal_document: 'Legal Document',
    w2: 'W-2',
    personal_financial_statement: 'Personal Financial Statement (PFS)',
    paystub: 'Paystub',
    other: 'Other'
  };
  return labels[type] ?? 'Other';
}

export function getDocumentTypesByOwnership(ownershipType: DocumentOwnershipType): DocumentType[] {
  const buyerTypes: DocumentType[] = [
    'tax_return',
    'bank_statement',
    'w2',
    'personal_financial_statement',
    'paystub',
    'financial_statement',
    'insurance_document',
    'legal_document',
    'other'
  ];
  
  const sellerTypes: DocumentType[] = [
    'tax_return',
    'profit_loss_statement',
    'balance_sheet',
    'inventory_list',
    'lease_agreement',
    'bank_statement',
    'financial_statement',
    'insurance_document',
    'legal_document',
    'other'
  ];
  
  return ownershipType === 'buyer' ? buyerTypes : sellerTypes;
}

export function getDocumentStatusLabel(status: DocumentStatus): string {
  const labels: Record<DocumentStatus, string> = {
    draft: 'Draft',
    pending_review: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    archived: 'Archived'
  };
  return labels[status] ?? 'Unknown';
}

export function getDocumentStatusColor(status: DocumentStatus): string {
  const colors: Record<DocumentStatus, string> = {
    draft: 'bg-gray-100 text-gray-800',
    pending_review: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    archived: 'bg-blue-100 text-blue-800'
  };
  return colors[status] ?? 'bg-gray-100 text-gray-800';
}

export function getDocumentAccessLevelLabel(level: DocumentAccessLevel): string {
  const labels: Record<DocumentAccessLevel, string> = {
    private: 'Private',
    role_based: 'Role-Based',
    shared: 'Shared',
    public: 'Public'
  };
  return labels[level] ?? 'Unknown';
}

export function getDocumentAccessLevelColor(level: DocumentAccessLevel): string {
  const colors: Record<DocumentAccessLevel, string> = {
    private: 'bg-red-100 text-red-800',
    role_based: 'bg-blue-100 text-blue-800',
    shared: 'bg-yellow-100 text-yellow-800',
    public: 'bg-green-100 text-green-800'
  };
  return colors[level] ?? 'bg-gray-100 text-gray-800';
}

export function getDocumentOwnershipTypeLabel(type: DocumentOwnershipType): string {
  const labels: Record<DocumentOwnershipType, string> = {
    buyer: 'Buyer',
    seller: 'Seller'
  };
  return labels[type] ?? 'Other';
}

export function getDocumentOwnershipTypeColor(type: DocumentOwnershipType): string {
  const colors: Record<DocumentOwnershipType, string> = {
    buyer: 'bg-purple-100 text-purple-800',
    seller: 'bg-green-100 text-green-800'
  };
  return colors[type] ?? 'bg-gray-100 text-gray-800';
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Deal Management Types
export type DealStatus = 'active' | 'inactive' | 'completed' | 'cancelled';

export type DealAccessType = 'read' | 'write' | 'admin';

export interface Deal {
  id: string;
  business_name: string;
  business_alias?: string;
  description?: string;
  creator: string;
  status: DealStatus;
  deal_value?: number;
  currency: string;
  industry?: string;
  business_description?: string;
  business_type?: string;
  employee_count?: string;
  years_in_business?: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;
  is_active: boolean;
}

export interface DealAccess {
  id: string;
  deal_id: string;
  user_id?: string;
  role?: UserRole;
  access_type: DealAccessType;
  granted_by: string;
  granted_at: string;
  expires_at?: string;
  is_active: boolean;
  metadata?: Record<string, any>;
  // Additional fields from get_deal_access_with_owner function
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
  granter_email?: string;
  granter_first_name?: string;
  granter_last_name?: string;
  is_owner?: boolean;
}

export interface DealFinancialData {
  id: string;
  deal_id: string;
  data_type: string;
  period_start: string;
  period_end: string;
  amount: number;
  currency: string;
  metadata?: Record<string, any>;
  created_at: string;
  updated_at: string;
  created_by: string;
  is_active: boolean;
}

export interface DealDocument {
  id: string;
  deal_id: string;
  document_id: string;
  document_type: string;
  is_primary: boolean;
  created_at: string;
  created_by: string;
}

export interface DealSummary {
  id: string;
  business_name: string;
  description?: string;
  creator: string;
  buyer_email: string;
  buyer_first_name?: string;
  buyer_last_name?: string;
  status: DealStatus;
  deal_value?: number;
  currency: string;
  created_at: string;
  updated_at: string;
  access_count: number;
  document_count: number;
  financial_record_count: number;
}

export interface UserDeal {
  deal_id: string;
  business_name: string;
  business_alias?: string;
  description?: string;
  creator: string;
  status: DealStatus;
  deal_value?: number;
  currency: string;
  created_at: string;
  updated_at: string;
  access_type: DealAccessType;
  // NDA fields
  nda_status?: string;
  has_nda?: boolean;
  nda_id?: string;
  nda_signed?: boolean;
  // Pending NDA fields
  is_pending_nda?: boolean;
  invitation_id?: string;
  inviter_name?: string;
  inviter_email?: string;
}

// Deal API Request/Response Types
export interface CreateDealRequest {
  business_name: string;
  description?: string;
  deal_value?: number;
  currency?: string;
  industry?: string;
  business_description?: string;
  business_type?: string;
  employee_count?: string;
  years_in_business?: string;
  metadata?: Record<string, any>;
}

export interface UpdateDealRequest {
  id: string;
  business_name?: string;
  business_alias?: string;
  description?: string;
  status?: DealStatus;
  deal_value?: number;
  currency?: string;
  industry?: string;
  business_description?: string;
  business_type?: string;
  employee_count?: string;
  years_in_business?: string;
  metadata?: Record<string, any>;
}

export interface GrantDealAccessRequest {
  deal_id: string;
  user_id?: string;
  role?: UserRole;
  access_type: DealAccessType;
  expires_at?: string;
}

export interface RevokeDealAccessRequest {
  deal_id: string;
  user_id?: string;
  role?: UserRole;
}

export interface CreateDealFinancialDataRequest {
  deal_id: string;
  data_type: string;
  period_start: string;
  period_end: string;
  amount: number;
  currency?: string;
  metadata?: Record<string, any>;
}

export interface CreateDealDocumentRequest {
  deal_id: string;
  document_id: string;
  document_type: string;
  is_primary?: boolean;
}

// Deal utility functions
export function getDealStatusLabel(status: DealStatus): string {
  const labels: Record<DealStatus, string> = {
    active: 'Active',
    inactive: 'Inactive',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };
  return labels[status] ?? 'Unknown';
}

export function getDealStatusColor(status: DealStatus): string {
  const colors: Record<DealStatus, string> = {
    active: 'bg-green-100 text-green-800',
    inactive: 'bg-gray-100 text-gray-800',
    completed: 'bg-blue-100 text-blue-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  return colors[status] ?? 'bg-gray-100 text-gray-800';
}

export function getDealAccessTypeLabel(accessType: DealAccessType): string {
  const labels: Record<DealAccessType, string> = {
    read: 'Read Only',
    write: 'Read & Write',
    admin: 'Full Access'
  };
  return labels[accessType] ?? 'Unknown';
}

export function getDealAccessTypeColor(accessType: DealAccessType): string {
  const colors: Record<DealAccessType, string> = {
    read: 'bg-blue-100 text-blue-800',
    write: 'bg-yellow-100 text-yellow-800',
    admin: 'bg-red-100 text-red-800'
  };
  return colors[accessType] ?? 'bg-gray-100 text-gray-800';
}

export function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
}

// Proforma types
export interface Proforma {
  id: string;
  deal_id: string;
  deal_scenario_id: string;
  title: string;
  description?: string;
  status: 'draft' | 'in_progress' | 'completed' | 'archived';
  version: string;
  created_by?: string;
  sales_narrative?: string;
  sales_before: number;
  sales_after: number;
  sales_net_change: number;
  sales_percentage_change: number;
  cogs_narrative?: string;
  cogs_before: number;
  cogs_after: number;
  cogs_net_change: number;
  cogs_percentage_change: number;
  cogs_as_percent_of_sales_before: number;
  cogs_as_percent_of_sales_after: number;
  expenses_narrative?: string;
  expenses_before: number;
  expenses_after: number;
  expenses_net_change: number;
  expenses_percentage_change: number;
  expense_categories: any[];
  timeline_items: any[];
  projections: Record<string, any>;
  growth_assumptions: Record<string, any>;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  metadata: Record<string, any>;
}

export interface CreateProformaRequest {
  deal_id: string;
  deal_scenario_id: string;
  title?: string;
  description?: string;
  status?: 'draft' | 'in_progress' | 'completed' | 'archived';
  version?: string;
  sales_narrative?: string;
  sales_before?: number;
  sales_after?: number;
  cogs_narrative?: string;
  cogs_before?: number;
  cogs_after?: number;
  expenses_narrative?: string;
  expenses_before?: number;
  expenses_after?: number;
  expense_categories?: any[];
  timeline_items?: any[];
  projections?: Record<string, any>;
  growth_assumptions?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface UpdateProformaRequest {
  id: string;
  title?: string;
  description?: string;
  status?: 'draft' | 'in_progress' | 'completed' | 'archived';
  version?: string;
  sales_narrative?: string;
  sales_before?: number;
  sales_after?: number;
  cogs_narrative?: string;
  cogs_before?: number;
  cogs_after?: number;
  expenses_narrative?: string;
  expenses_before?: number;
  expenses_after?: number;
  expense_categories?: any[];
  timeline_items?: any[];
  projections?: Record<string, any>;
  growth_assumptions?: Record<string, any>;
  metadata?: Record<string, any>;
}
